/**
 * Iris Answer Inspection — the per-turn "why this answer" trace for the AI
 * service agent. For every AI reply in a conversation we persist what shaped
 * it: the grounded knowledge snippets retrieved (with relevance scores), the
 * guidance rules that fired, the tools the agent called (args → result), the
 * model's confidence, and which bot role handled the turn.
 *
 * This mirrors Intercom Fin's Answer Inspection panel. Reached through the
 * operations proxy at `/api/messaging/iris/inspection` (operations is the FE's
 * auth boundary + RBAC gate; it forwards to ai-operations with the shared
 * service key), mirroring the sibling `receptionists.ts` / `review.ts` hooks.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/inspection';

/** A grounded knowledge snippet that was retrieved for a turn. `score` is the
 * retrieval relevance (0–1) when the retriever provides one. */
export interface TraceSource {
  title: string | null;
  text: string;
  /** Retrieval relevance score (0–1); null when the retriever gives none. */
  score: number | null;
}

/** A guidance rule (active tenant guidance) that shaped the turn's answer. */
export interface TraceGuidance {
  /** The guidance text/rule that applied. */
  text: string;
  /** Optional short label/category for the rule. */
  label: string | null;
}

/** A tool the agent invoked during the turn, with the arguments it passed and
 * the result it got back. `args`/`result` are opaque JSON echoed for display. */
export interface TraceTool {
  name: string;
  args: unknown;
  result: unknown;
  /** True when the tool call failed (result is an error). */
  isError: boolean;
}

/** A piece of tenant configuration that shaped a reply (not retrieved knowledge). */
export interface TraceContextSource {
  /** jobType | serviceType | pricebook | serviceArea | businessHours | config */
  kind: string;
  /** What it is — a Job Type name, or a summary like "3 service areas". */
  label: string;
  /** Qualifier, e.g. "Job Type · LISTED". */
  detail: string | null;
}

/** One AI turn's inspection trace — everything that shaped a single reply. */
export interface TurnTrace {
  /** The OUTBOUND SmsMessage id this trace explains. */
  messageId: string;
  createdAt: string;
  /** Bot role that produced the reply (e.g. receptionist / specialist name). */
  handledByRole: string | null;
  /** The inbound customer message that prompted this reply, if captured. */
  userMessage: string | null;
  /** The AI reply text this trace explains. */
  replyText: string | null;
  /**
   * Where the turn's time went, in milliseconds by stage (turnMs, ttftMs,
   * routerMs, toolLoopMs, lockWaitedMs …) plus a few facts about the turn
   * (model, tools, deterministicReply). Absent on traces written before the
   * column existed.
   */
  timings?: Record<string, number | string> | null;
  /** Whether the answer was grounded in retrieved knowledge. */
  grounded: boolean;
  /** Model self-reported confidence (0–1); null when not captured. */
  confidence: number | null;
  /** The agent's why-summary for this turn (why it chose this step/tool); null when not captured. */
  reasoning: string | null;
  /** Id of the procedure (SOP) that was active for this turn, if any. */
  procedureId: string | null;
  /** Knowledge snippets retrieved for this turn. */
  sources: TraceSource[];
  /**
   * Tenant configuration the prompt CARRIED into this turn — the Job Type
   * catalog, hours, service areas, pricebook. Distinct from `sources`
   * (retrieved knowledge), and deliberately a weaker claim than either: it says
   * what was available to the model, not what the answer used. Empty on
   * deterministic FLOW turns, which never assemble that prompt.
   */
  contextSources: TraceContextSource[];
  /** Active guidance rules that applied to this turn. */
  guidance: TraceGuidance[];
  /** Tools the agent called during this turn. */
  tools: TraceTool[];
}

/** All AI-turn traces for a conversation, newest-relevant order preserved. */
export interface ConversationTraces {
  conversationId: string;
  turns: TurnTrace[];
}

export const conversationTracesKey = (conversationId: string | null) =>
  ['messaging', 'iris-inspection', conversationId] as const;

/**
 * Fetch the per-turn Answer Inspection traces for a conversation. Disabled
 * until a `conversationId` is provided.
 */
export function useConversationTraces(conversationId: string | null) {
  return useQuery({
    queryKey: conversationTracesKey(conversationId),
    enabled: !!conversationId,
    queryFn: () =>
      apiGet<ConversationTraces>(
        operationsClient,
        `${BASE}/conversation/${encodeURIComponent(conversationId as string)}`,
      ),
    staleTime: 30_000,
  });
}
